// src/components/LightingCanvas.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Light, Room } from '../types/Light';
import { ScaledPosition } from '../types/Dimensions';

interface LightingCanvasProps {
  width: number;
  height: number;
  lights: Light[];
  rooms: Room[];
  scaling: ScaledPosition;
  originalImageDimensions: { width: number; height: number };
  backgroundSrc: string;
}

export const LightingCanvas: React.FC<LightingCanvasProps> = ({
  width,
  height,
  lights,
  rooms,
  scaling,
  originalImageDimensions,
  backgroundSrc,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const DEBUG = false;

  useEffect(() => {
    const img = new Image();
    img.src = backgroundSrc;
    img.onload = () => {
      setBackgroundImage(img);
    };
  }, [backgroundSrc]);

  const drawLight = (
    ctx: CanvasRenderingContext2D,
    light: Light
  ) => {
    // Core highlight (small, bright white center)
    const coreGradient = ctx.createRadialGradient(
      light.position.x,
      light.position.y,
      0,
      light.position.x,
      light.position.y,
      light.properties.radius * 0.1
    );
    coreGradient.addColorStop(0, `rgba(255, 255, 255, ${light.properties.intensity})`);
    coreGradient.addColorStop(1, 'transparent');

    // Main light color
    const mainGradient = ctx.createRadialGradient(
      light.position.x,
      light.position.y,
      light.properties.radius * 0.1,
      light.position.x,
      light.position.y,
      light.properties.radius * 0.8
    );

    const colorMatch = light.properties.color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const rgb = colorMatch
      ? {
        r: parseInt(colorMatch[1], 16),
        g: parseInt(colorMatch[2], 16),
        b: parseInt(colorMatch[3], 16)
      }
      : { r: 255, g: 255, b: 255 };

    const innerColor = `rgba(${Math.min(rgb.r + 40, 255)}, ${Math.min(rgb.g + 40, 255)}, ${Math.min(rgb.b + 40, 255)}, ${light.properties.intensity})`;

    mainGradient.addColorStop(0, innerColor);
    mainGradient.addColorStop(0.6, `${light.properties.color}${Math.floor(light.properties.intensity * 255).toString(16).padStart(2, '0')}`);
    mainGradient.addColorStop(1, 'transparent');

    // Outer glow
    const outerGradient = ctx.createRadialGradient(
      light.position.x,
      light.position.y,
      light.properties.radius * 0.8,
      light.position.x,
      light.position.y,
      light.properties.radius
    );
    outerGradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${light.properties.intensity * 0.3})`);
    outerGradient.addColorStop(1, 'transparent');

    // Draw each gradient layer
    ctx.globalCompositeOperation = 'screen';

    // Draw outer glow
    ctx.fillStyle = outerGradient;
    ctx.beginPath();
    ctx.arc(
      light.position.x,
      light.position.y,
      light.properties.radius,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Draw main light
    ctx.fillStyle = mainGradient;
    ctx.beginPath();
    ctx.arc(
      light.position.x,
      light.position.y,
      light.properties.radius * 0.8,
      0,
      Math.PI * 2
    );
    ctx.fill();

    // Draw core
    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(
      light.position.x,
      light.position.y,
      light.properties.radius * 0.1,
      0,
      Math.PI * 2
    );
    ctx.fill();
  };

  const createLightMask = (
    ctx: CanvasRenderingContext2D,
    light: Light
  ) => {
    const gradient = ctx.createRadialGradient(
      light.position.x,
      light.position.y,
      0,
      light.position.x,
      light.position.y,
      light.properties.radius * 1.2
    );

    gradient.addColorStop(0, `rgba(255, 255, 255, ${light.properties.intensity * 0.9})`);
    gradient.addColorStop(0.7, `rgba(255, 255, 255, ${light.properties.intensity * 0.5})`);
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(
      light.position.x,
      light.position.y,
      light.properties.radius * 1.2,
      0,
      Math.PI * 2
    );
    ctx.fill();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !backgroundImage) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Create separate canvases for layers
    const darkCanvas = document.createElement('canvas');
    const brightCanvas = document.createElement('canvas');
    const maskCanvas = document.createElement('canvas');
    const lightCanvas = document.createElement('canvas');

    [darkCanvas, brightCanvas, maskCanvas, lightCanvas].forEach(c => {
      c.width = width;
      c.height = height;
    });

    const darkCtx = darkCanvas.getContext('2d')!;
    const brightCtx = brightCanvas.getContext('2d')!;
    const maskCtx = maskCanvas.getContext('2d')!;
    const lightCtx = lightCanvas.getContext('2d')!;

    // Draw dark background
    darkCtx.save();
    darkCtx.setTransform(scaling.scale, 0, 0, scaling.scale, scaling.x, scaling.y);
    darkCtx.filter = 'grayscale(100%) brightness(0.3)';
    darkCtx.drawImage(backgroundImage, 0, 0);
    darkCtx.restore();

    // Draw bright background
    brightCtx.save();
    brightCtx.setTransform(scaling.scale, 0, 0, scaling.scale, scaling.x, scaling.y);
    brightCtx.filter = 'grayscale(100%) brightness(0.8)';
    brightCtx.drawImage(backgroundImage, 0, 0);
    brightCtx.restore();

    // Create light mask
    maskCtx.save();
    maskCtx.setTransform(scaling.scale, 0, 0, scaling.scale, scaling.x, scaling.y);

    rooms.forEach(room => {
      const roomLights = lights.filter(light => light.roomId === room.id);

      if (roomLights.length > 0) {
        maskCtx.save();

        // Clip to room bounds
        maskCtx.beginPath();
        if (room.path) {
          maskCtx.clip(room.path);
        } else {
          maskCtx.rect(
            room.bounds.x,
            room.bounds.y,
            room.bounds.width,
            room.bounds.height
          );
        }
        maskCtx.clip();

        // Draw light masks
        maskCtx.globalCompositeOperation = 'lighter';
        roomLights.forEach(light => createLightMask(maskCtx, light));

        maskCtx.restore();
      }
    });
    maskCtx.restore();

    // Draw lights
    lightCtx.save();
    lightCtx.setTransform(scaling.scale, 0, 0, scaling.scale, scaling.x, scaling.y);
    rooms.forEach(room => {
      const roomLights = lights.filter(light => light.roomId === room.id);

      if (roomLights.length > 0) {
        lightCtx.save();

        // Clip to room bounds
        lightCtx.beginPath();
        if (room.path) {
          lightCtx.clip(room.path);
        } else {
          lightCtx.rect(
            room.bounds.x,
            room.bounds.y,
            room.bounds.width,
            room.bounds.height
          );
        }
        lightCtx.clip();

        // Draw lights
        roomLights.forEach(light => drawLight(lightCtx, light));

        lightCtx.restore();
      }
    });
    lightCtx.restore();

    // Composite all layers
    ctx.drawImage(darkCanvas, 0, 0); // Base dark layer

    // Apply masked bright layer
    ctx.globalCompositeOperation = 'lighter';
    brightCtx.globalCompositeOperation = 'destination-in';
    brightCtx.drawImage(maskCanvas, 0, 0);
    ctx.drawImage(brightCanvas, 0, 0);

    // Add light effects
    ctx.drawImage(lightCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    // Debug: Draw room boundaries
    if (DEBUG) {
      ctx.save();
      ctx.setTransform(scaling.scale, 0, 0, scaling.scale, scaling.x, scaling.y);
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.lineWidth = 2 / scaling.scale;
      rooms.forEach(room => {
        ctx.strokeRect(
          room.bounds.x,
          room.bounds.y,
          room.bounds.width,
          room.bounds.height
        );
      });
      ctx.restore();
    }

  }, [width, height, lights, rooms, scaling, backgroundImage, originalImageDimensions]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};